'use client';

import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  TextInput,
  Title,
  Text,
  Stack,
  Paper,
  rem,
  Select,
  Group,
  Center
} from '@mantine/core';
import { useRouter } from 'next/navigation';
import { useSectionContent } from '@/hooks/useSectionContent';
import { UserRole, FAQItem } from '@/utils/types/faq';
import SiteHeader from '@/components/SiteHeader';
import PageTitle from '@/components/PageTitle';
import SiteFooter from '@/components/SiteFooter';
import Accordion from '@/components/Accordion';
import FAIcon from '@/components/FAIcon';
import { useResponsive } from '@/hooks/use-responsive';
import Button from '@/components/Button';

interface Question extends FAQItem {
  role?: string;
  category?: string;
}

const FaqPage = () => {
  const router = useRouter();
  const { responsive, screen } = useResponsive();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const { content } = useSectionContent('faq') as unknown as { content: { users: UserRole[] } };

  const categories = useMemo(() => {
    if (!content) return [];
    const allCategories = content.users.flatMap(user =>
      user.categories.map(category => category.title)
    );
    return [...new Set(allCategories)];
  }, [content]);

  const groupedQuestions = useMemo(() => {
    if (!content) return [];

    return content.users.map(user => ({
      ...user,
      categories: user.categories
        .filter(category => !selectedCategory || category.title === selectedCategory)
        .map(category => ({
          ...category,
          questions: category.questions.filter((q: Question) => {
            const query = searchQuery.toLowerCase();
            const questionText = String(q.question).toLowerCase();
            const answerText = String(q.answer).toLowerCase();
            return !query || questionText.includes(query) || answerText.includes(query);
          })
        })).filter(cat => cat.questions.length > 0)
    })).filter(user => user.categories.length > 0);
  }, [content, searchQuery, selectedCategory]);

  const hasResults = groupedQuestions.length > 0;

  if (!content) return null;

  const spacing = responsive({
    xs: 40,
    sm: 40,
    md: 60,
    lg: 80,
    xl: 80
  });

  return (
    <>
      <SiteHeader scheme="dark" />
      <Box mih="100vh" bg="white">
        <PageTitle
          subtitle="How can we help?"
          title="Frequently Asked Questions"
          tagline="Get quick answers to common questions about our services."
          pt={responsive({ xs: rem(80), sm: rem(80), md: rem(120), lg: rem(120), xl: rem(120) })}
          pb={responsive({ xs: rem(40), sm: rem(40), md: rem(80), lg: rem(80), xl: rem(80) })}
          bg="gray.0"
        />

        <Box component="div" pos="sticky" top={0} bg="white">
          <Container size="md" py="md">
            <Group align="flex-start" gap="md">
              <TextInput
                leftSection={<FAIcon name='search' size={20} />}
                placeholder="Search for answers..."
                size="lg"
                radius="md"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.currentTarget.value)}
                style={{ flex: 1 }}
              />
              <Select
                data={[
                  { value: '', label: 'All Categories' },
                  ...categories.map(cat => ({ value: cat, label: cat }))
                ]}
                value={selectedCategory}
                onChange={(value) => setSelectedCategory(value || '')}
                size="lg"
                radius="md"
                placeholder="Filter by category"
                style={{ minWidth: rem(200) }}
              />
            </Group>
          </Container>
        </Box>

        <Container size="md" py={spacing}>
          {hasResults ? (
            <Stack gap={responsive({ xs: 'md', sm: 'lg', md: 'xl', lg: 'xl', xl: 'xl' })}>
              {groupedQuestions.map((user, userIndex) => (
                <React.Fragment key={userIndex}>
                  {user.categories.map((category, categoryIndex) => (
                    <Paper
                      key={`${userIndex}-${categoryIndex}`}
                      p={responsive({ xs: 'md', sm: 'lg', md: 'xl', lg: 'xl', xl: 'xl' })}
                      radius="md"
                      shadow="sm"
                      style={{
                        transition: 'transform 200ms ease',
                        '&:hover': {
                          transform: 'translateY(-4px)'
                        }
                      }}
                    >
                      <Title
                        order={2}
                        mb="md"
                        pb="xs"
                        style={{
                          borderBottom: '2px solid var(--color-primary)',
                          fontSize: responsive({
                            xs: rem(20),
                            sm: rem(22),
                            md: rem(24),
                            lg: rem(24),
                            xl: rem(24)
                          }),
                          fontWeight: 700
                        }}
                      >
                        {category.title}
                      </Title>

                      <Accordion data={category.questions} />
                    </Paper>
                  ))}
                </React.Fragment>
              ))}
            </Stack>
          ) : (
            <Center py={spacing}>
              <Stack align="center" gap="lg">
                <FAIcon name="search-minus" size={64} style={{ color: 'var(--mantine-color-gray-4)' }} />
                <Title order={2} ta="center">No Results Found</Title>
                <Text size="lg" ta="center" c="dimmed" maw={500}>
                  We couldn&#39;t find any answers matching your search. Please try different keywords or get in touch with our support team for assistance.
                </Text>
                <Button
                  size="medium"
                  radius="md"
                  component='a'
                  href='/contact'
                  hasArrow
                  variant='outline'
                >
                  <FAIcon name="envelope" size={22} mr={10} />
                  Contact Support
                </Button>
              </Stack>
            </Center>
          )}
        </Container>
      </Box>
      <SiteFooter />
    </>
  );
};

export default FaqPage;